
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  addDoc,
  setDoc,
  doc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

const DEFAULT_FORM = {
  // BASICS
  name: "",
  description: "",
  // CONTEXT
  guidelines: "",
  tone: "",
  // SAFETY (kept in data model but UI removed)
  forbiddenTopics: "",
  // TOOLS (kept in data model but UI removed)
  enableTools: {
    search: false,
    math: false,
    memory: false,
  },
};

export default function AgentFormDialog({
  open,
  onClose,
  tenantId,
  initialAgent = null, // null => create
}) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const CONTEXT_TOKEN_LIMIT = 5000; // acceptable cap for system prompt context

  const isEdit = Boolean(initialAgent?.id);

  // Hydrate form when dialog opens or initialAgent changes
  useEffect(() => {
    if (!open) return;
    setErr("");
    setTab(0);

    if (isEdit) {
      const a = initialAgent || {};
      setForm({
        name: a.name || "",
        description: a.description || "",
        guidelines: a.guidelines || "",
        tone: a.tone || "",
        forbiddenTopics: a.forbiddenTopics || "",
        enableTools: {
          search: Boolean(a?.enableTools?.search),
          math: Boolean(a?.enableTools?.math),
          memory: Boolean(a?.enableTools?.memory),
        },
      });
    } else {
      setForm(DEFAULT_FORM);
    }
  }, [open, isEdit, initialAgent]);

  const canSave = useMemo(
    () => {
      const estimateTokens = (s) => Math.ceil(String(s || "").length / 4);
      const ctxTokens = estimateTokens(form.guidelines) +
        estimateTokens(form.tone) +
        estimateTokens(form.forbiddenTopics);
      return Boolean(tenantId) && form.name.trim().length > 0 && !saving && ctxTokens <= CONTEXT_TOKEN_LIMIT;
    },
    [tenantId, form.name, saving, form.guidelines, form.tone, form.forbiddenTopics]
  );

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setErr("");
    if (!tenantId) {
      setErr("Missing tenantId; please reload.");
      return;
    }
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      if (isEdit) {
        // UPDATE: tenants/{tenantId}/agents/{agentId}
        const ref = doc(db, "tenants", tenantId, "agents", initialAgent.id);
        await setDoc(
          ref,
          {
            // tout le contenu du form
            name: form.name.trim(),
            description: form.description.trim(),
            guidelines: form.guidelines.trim(),
            tone: form.tone.trim(),
            forbiddenTopics: form.forbiddenTopics.trim(),
            enableTools: {
              search: Boolean(form.enableTools.search),
              math: Boolean(form.enableTools.math),
              memory: Boolean(form.enableTools.memory),
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } else {
        // CREATE: tenants/{tenantId}/agents
        const colRef = collection(db, "tenants", tenantId, "agents");
        await addDoc(colRef, {
          name: form.name.trim(),
          description: form.description.trim(),
          guidelines: form.guidelines.trim(),
          tone: form.tone.trim(),
          forbiddenTopics: form.forbiddenTopics.trim(),
          enableTools: {
            search: Boolean(form.enableTools.search),
            math: Boolean(form.enableTools.math),
            memory: Boolean(form.enableTools.memory),
          },
          status: "active",
          totalInteractions: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      onClose?.();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to save agent.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{isEdit ? "Edit Agent" : "New Agent"}</DialogTitle>

      <DialogContent dividers>
        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2 }}
          variant="scrollable"
        >
          <Tab label="Basics" />
          <Tab label="Context" />
        </Tabs>

        {/* BASICS */}
        {tab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              autoFocus
              label="Agent Name"
              placeholder="e.g., Rouen Real-Estate Concierge"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="Short Description"
              placeholder="What this agent does in one or two sentences."
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              fullWidth
              multiline
              minRows={3}
              disabled={saving}
            />
          </Box>
        )}

        {/* CONTEXT */}
        {tab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {(() => {
              const estimateTokens = (s) => Math.ceil(String(s || "").length / 4);
              const ctxTokens = estimateTokens(form.guidelines) +
                estimateTokens(form.tone) +
                estimateTokens(form.forbiddenTopics);
              const over = ctxTokens > CONTEXT_TOKEN_LIMIT;
              return (
                <Alert severity={over ? "warning" : "info"} sx={{ py: 0, alignItems: "center" }}>
                  Context tokens: {ctxTokens} / {CONTEXT_TOKEN_LIMIT}
                </Alert>
              );
            })()}
            <TextField
              label="Guidelines / Persona"
              placeholder="How should this agent behave, speak, or specialize?"
              multiline
              minRows={3}
              value={form.guidelines}
              onChange={(e) => handleChange("guidelines", e.target.value)}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="Tone"
              placeholder="Friendly, professional, concise..."
              value={form.tone}
              onChange={(e) => handleChange("tone", e.target.value)}
              fullWidth
              disabled={saving}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : null
          }
        >
          {isEdit ? "Save changes" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
